import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocationContext } from '../context/LocationContext';
import { DateRangeCalendar } from '../components/sync-tracking/DateRangeCalendar';
import { DonutChart, GroupedBarChart, StackedShareBar, Sparkline, type ChartSlice } from '../components/sync-analytics/SimpleCharts';
import {
    EngineUnavailableError,
    syncTrackingApi,
    type AnalyticsDetailScope,
    type SyncAnalyticsDetailResponse,
    type SyncAnalyticsResponse,
} from '../api/syncTrackingApi';
import { dirView, statusView } from '../lib/syncTrackingMeta';
import { AnalyticsHelpDialog } from '../components/sync-analytics/AnalyticsHelpDialog';

const MAX_DATE_SPAN_DAYS = 90;

type EntityFilter = 'all' | 'patient' | 'appointment';
type DirFilter = 'all' | 'emr_to_ghl' | 'ghl_to_emr';

interface Filters {
    entity: EntityFilter;
    direction: DirFilter;
    dateFrom: string;
    dateTo: string;
}

interface DetailRequest {
    scope: AnalyticsDetailScope;
    scopeKey: string;
}

const OUTCOME_COLORS: Record<string, string> = {
    succeeded: '#0E8577',
    skipped: '#8A94A3',
    retrying: '#D69E2E',
    failed: '#D6607A',
};
const DIRECTION_COLORS: Record<string, string> = {
    emr_to_ghl: '#0E8577',
    ghl_to_emr: '#6A62B5',
};
const ENTITY_COLORS: Record<string, string> = {
    patient: '#3B5BA9',
    appointment: '#0E8577',
};

function daySpan(from: string, to: string): number {
    const a = new Date(`${from}T00:00:00Z`).getTime();
    const b = new Date(`${to}T00:00:00Z`).getTime();
    return Math.round((b - a) / 86400000);
}

function formatShortDate(iso: string): string {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
}

function utcTodayISO(): string {
    const now = new Date();
    return [
        now.getUTCFullYear(),
        String(now.getUTCMonth() + 1).padStart(2, '0'),
        String(now.getUTCDate()).padStart(2, '0'),
    ].join('-');
}

function isoDaysAgo(days: number, end: string): string {
    const d = new Date(`${end}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - (days - 1));
    return d.toISOString().slice(0, 10);
}

function defaultFilters(): Filters {
    const dateTo = utcTodayISO();
    return {
        entity: 'all',
        direction: 'all',
        dateFrom: isoDaysAgo(14, dateTo),
        dateTo,
    };
}

function colorize(slices: { key: string; label: string; value: number }[], palette: Record<string, string>, fallback = '#8A94A3'): ChartSlice[] {
    return slices.map(s => ({ ...s, color: palette[s.key] ?? fallback }));
}

const PRESETS: { label: string; days: number }[] = [
    { label: '7d', days: 7 },
    { label: '14d', days: 14 },
    { label: '30d', days: 30 },
];

const CalendarIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" />
    </svg>
);

const EMPTY: SyncAnalyticsResponse = {
    daily: [],
    kpis: {
        total_synced: 0,
        success_rate: 0,
        failed: 0,
        in_progress: 0,
        avg_sync_seconds: null,
        patients_synced: 0,
        appointments_synced: 0,
    },
    outcomes: [],
    directions: [],
    entities: [],
    peak: null,
};

export const SyncAnalyticsPage: React.FC = () => {
    const { currentLocation, locationId } = useLocationContext();
    const emr = currentLocation?.emr ?? '';

    const [filters, setFilters] = useState<Filters>(defaultFilters);
    const [menuOpen, setMenuOpen] = useState<'date' | null>(null);
    const [draftFrom, setDraftFrom] = useState(filters.dateFrom);
    const [draftTo, setDraftTo] = useState(filters.dateTo);
    const [barMode, setBarMode] = useState<'entity' | 'direction' | 'outcome'>('entity');

    const [data, setData] = useState<SyncAnalyticsResponse>(EMPTY);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [engineUnavailable, setEngineUnavailable] = useState(false);

    const [detailReq, setDetailReq] = useState<DetailRequest | null>(null);
    const [detail, setDetail] = useState<SyncAnalyticsDetailResponse | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const dateMenuRef = useRef<HTMLDivElement>(null);
    const detailPanelRef = useRef<HTMLDivElement>(null);

    const setF = <K extends keyof Filters>(k: K, v: Filters[K]) => {
        setFilters(f => ({ ...f, [k]: v }));
    };

    // Close the date panel on outside click or Escape.
    useEffect(() => {
        if (!menuOpen) return;
        const onPointerDown = (e: MouseEvent) => {
            if (dateMenuRef.current && !dateMenuRef.current.contains(e.target as Node)) {
                setMenuOpen(null);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(null);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    // Escape closes the drill-down; scroll it into view when opened.
    useEffect(() => {
        if (!detailReq) return;
        detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setDetailReq(null);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [detailReq]);

    // Close drill-down when the main filters change so numbers stay consistent.
    useEffect(() => {
        setDetailReq(null);
        setDetail(null);
        setDetailError(null);
    }, [locationId, emr, filters.entity, filters.direction, filters.dateFrom, filters.dateTo]);

    // Load full breakdown for the clicked slice / day.
    useEffect(() => {
        if (!detailReq || !locationId || !emr || !filters.dateFrom || !filters.dateTo) {
            return;
        }

        let cancelled = false;
        setDetailLoading(true);
        setDetailError(null);

        syncTrackingApi.analyticsDetail(locationId, emr, {
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            entity: filters.entity === 'all' ? undefined : filters.entity,
            direction: filters.direction === 'all' ? undefined : filters.direction,
            scope: detailReq.scope,
            scopeKey: detailReq.scopeKey,
        })
            .then(resp => {
                if (!cancelled) setDetail(resp);
            })
            .catch(err => {
                if (cancelled) return;
                setDetail(null);
                setDetailError(err instanceof Error ? err.message : 'Failed to load details');
            })
            .finally(() => {
                if (!cancelled) setDetailLoading(false);
            });

        return () => { cancelled = true; };
    }, [detailReq, locationId, emr, filters.entity, filters.direction, filters.dateFrom, filters.dateTo]);

    const dateError = useMemo(() => {
        if (draftFrom && draftTo && draftFrom > draftTo) return 'Start date must be on or before end date.';
        if (draftFrom && draftTo && daySpan(draftFrom, draftTo) > MAX_DATE_SPAN_DAYS) {
            return `Date range cannot exceed ${MAX_DATE_SPAN_DAYS} days.`;
        }
        return null;
    }, [draftFrom, draftTo]);

    const applyDate = () => {
        if (dateError || !draftFrom || !draftTo) return;
        setFilters(f => ({ ...f, dateFrom: draftFrom, dateTo: draftTo }));
        setMenuOpen(null);
    };

    const applyPreset = (days: number) => {
        const dateTo = utcTodayISO();
        const dateFrom = isoDaysAgo(days, dateTo);
        setFilters(f => ({ ...f, dateFrom, dateTo }));
        setDraftFrom(dateFrom);
        setDraftTo(dateTo);
        setMenuOpen(null);
    };

    useEffect(() => {
        if (!locationId || !emr || !filters.dateFrom || !filters.dateTo) {
            setData(EMPTY);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        setEngineUnavailable(false);

        syncTrackingApi.analytics(locationId, emr, {
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            entity: filters.entity === 'all' ? undefined : filters.entity,
            direction: filters.direction === 'all' ? undefined : filters.direction,
        })
            .then(resp => {
                if (!cancelled) setData(resp);
            })
            .catch(err => {
                if (cancelled) return;
                if (err instanceof EngineUnavailableError) {
                    setEngineUnavailable(true);
                    setData(EMPTY);
                    setError(null);
                } else {
                    setError(err instanceof Error ? err.message : 'Failed to load sync analytics');
                    setData(EMPTY);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [locationId, emr, filters.entity, filters.direction, filters.dateFrom, filters.dateTo]);

    const { daily, kpis, peak } = data;
    const outcomes = useMemo(() => colorize(data.outcomes, OUTCOME_COLORS), [data.outcomes]);
    const directions = useMemo(() => colorize(data.directions, DIRECTION_COLORS), [data.directions]);
    const entities = useMemo(() => colorize(data.entities, ENTITY_COLORS), [data.entities]);

    const hasVolume = daily.some(d =>
        d.succeeded + d.failed + d.ignored + d.in_progress + d.patients + d.appointments > 0,
    );

    const openDetail = (scope: AnalyticsDetailScope, scopeKey: string) => {
        setDetailReq({ scope, scopeKey });
    };

    const goToMapping = (route: string) => {
        window.location.hash = `/${route}`;
    };

    const rangeLabel =
        filters.dateFrom && filters.dateTo
            ? `${formatShortDate(filters.dateFrom)} – ${formatShortDate(filters.dateTo)}`
            : 'Select dates';

    const practiceName = currentLocation?.name ?? 'Your practice';
    const today = utcTodayISO();
    const activePreset = PRESETS.find(p =>
        filters.dateTo === today && filters.dateFrom === isoDaysAgo(p.days, today),
    );

    const avgLabel =
        kpis.avg_sync_seconds == null
            ? '—'
            : kpis.avg_sync_seconds < 60
                ? `${kpis.avg_sync_seconds}s`
                : `${Math.round(kpis.avg_sync_seconds / 6) / 10}m`;

    const segClass = (active: boolean) =>
        `px-[13px] py-[6px] rounded-lg text-[13px] transition-colors ${
            active ? 'bg-white text-mapper-teal-hover shadow-mapper-segmented font-semibold' : 'text-mapper-muted font-medium'
        }`;

    const dropdownBtnClass = (active: boolean) =>
        `flex items-center gap-[7px] px-3 py-2 border rounded-[10px] text-[13px] font-semibold ${
            active ? 'border-mapper-teal text-mapper-teal-hover bg-[#F2FAF8]' : 'border-mapper-border-2 text-mapper-body-text bg-white'
        }`;

    return (
        <div className="mapper-ui font-sans text-mapper-text">
            <div className="max-w-[1240px] mx-auto px-10 pt-[30px] pb-[70px]">
                <div className="flex items-end justify-between mb-[22px] gap-5">
                    <div>
                        <h1 className="text-[24px] font-extrabold tracking-[-0.5px] mb-[5px]">Sync Analytics</h1>
                        <p className="text-[14px] text-mapper-muted max-w-[640px]">
                            How patients and appointments are syncing for{' '}
                            <span className="font-semibold text-mapper-body-text-3">{practiceName}</span>
                            — success rates, volume by day, and which way data is flowing.
                        </p>
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end gap-2">
                        <button
                            type="button"
                            onClick={() => setHelpOpen(true)}
                            className="flex items-center gap-[6px] px-3 py-[7px] rounded-[9px] border border-mapper-border-2 bg-white text-[12.5px] font-semibold text-mapper-body-text hover:border-mapper-teal hover:text-mapper-teal-hover"
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <circle cx="12" cy="12" r="9" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4" /><path d="M12 17h.01" />
                            </svg>
                            Need help?
                        </button>
                        <div>
                            <div className="text-[12px] font-semibold text-mapper-muted-2 tracking-[0.3px] uppercase mb-1">Period</div>
                            <div className="text-[13.5px] font-semibold text-mapper-body-text-3">{rangeLabel}</div>
                            <div className="text-[12px] text-mapper-muted-3 mt-0.5">
                                {daily.length} day{daily.length === 1 ? '' : 's'}
                                {loading ? ' · loading…' : ''}
                            </div>
                        </div>
                    </div>
                </div>

                {engineUnavailable && (
                    <div className="border border-[#F0DFB8] bg-[#FFFDF7] rounded-[12px] px-4 py-3 mb-[22px] text-[13px] text-[#7A5A15]">
                        Sync analytics is unavailable — the engine database is not configured for this environment.
                    </div>
                )}
                {currentLocation && !currentLocation.sync_active && !engineUnavailable && (
                    <div className="border border-[#F0DFB8] bg-[#FFFDF7] rounded-[12px] px-4 py-3 mb-[22px] text-[13px] text-[#7A5A15]">
                        Sync is inactive for <strong className="font-bold">{currentLocation.name}</strong> — new records for this practice won&apos;t appear until sync is re-enabled.
                    </div>
                )}
                {error && (
                    <div className="border border-mapper-red-border bg-mapper-red-bg-2 rounded-[12px] px-4 py-3 mb-[22px] text-[13px] text-mapper-red">
                        {error}
                    </div>
                )}
                {!locationId && !loading && (
                    <div className="border border-mapper-border bg-mapper-white rounded-[12px] px-4 py-3 mb-[22px] text-[13px] text-mapper-muted">
                        Select a practice above to view analytics.
                    </div>
                )}

                {/* filters */}
                <div className="flex flex-col gap-[10px] mb-[22px]">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-[3px] p-[3px] bg-[#EEF1F5] rounded-[10px]">
                            {([
                                ['all', 'All entities'],
                                ['patient', 'Patients'],
                                ['appointment', 'Appointments'],
                            ] as [EntityFilter, string][]).map(([val, label]) => (
                                <button key={val} type="button" onClick={() => setF('entity', val)} className={segClass(filters.entity === val)}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-[3px] p-[3px] bg-[#EEF1F5] rounded-[10px]">
                            {([
                                ['all', 'Both directions'],
                                ['emr_to_ghl', 'EMR → Advital'],
                                ['ghl_to_emr', 'Advital → EMR'],
                            ] as [DirFilter, string][]).map(([val, label]) => (
                                <button key={val} type="button" onClick={() => setF('direction', val)} className={segClass(filters.direction === val)}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-2 ml-auto flex-wrap">
                            <div className="flex items-center gap-[3px] p-[3px] bg-[#EEF1F5] rounded-[10px]">
                                {PRESETS.map(p => (
                                    <button
                                        key={p.days}
                                        type="button"
                                        onClick={() => applyPreset(p.days)}
                                        className={segClass(activePreset?.days === p.days)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>

                            <div className="relative" ref={dateMenuRef}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDraftFrom(filters.dateFrom);
                                        setDraftTo(filters.dateTo);
                                        setMenuOpen(m => (m === 'date' ? null : 'date'));
                                    }}
                                    className={dropdownBtnClass(menuOpen === 'date' || (!!filters.dateFrom && !!filters.dateTo))}
                                >
                                    {CalendarIcon}
                                    <span>{filters.dateFrom && filters.dateTo ? rangeLabel : 'Date range'}</span>
                                </button>
                                {menuOpen === 'date' && (
                                    <DateRangeCalendar
                                        from={draftFrom}
                                        to={draftTo}
                                        error={dateError}
                                        onFromChange={setDraftFrom}
                                        onToChange={setDraftTo}
                                        onClear={() => { setDraftFrom(''); setDraftTo(''); }}
                                        onApply={applyDate}
                                        align="right"
                                        maxDaysHint={MAX_DATE_SPAN_DAYS}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* KPI tiles */}
                <div className={`grid grid-cols-2 lg:grid-cols-4 gap-[14px] mb-[22px] ${loading ? 'opacity-60' : ''}`}>
                    <KpiTile
                        label="Records synced"
                        value={kpis.total_synced.toLocaleString()}
                        sub={`${kpis.patients_synced.toLocaleString()} patients · ${kpis.appointments_synced.toLocaleString()} appts`}
                        spark={daily.map(d => d.succeeded + d.ignored)}
                        sparkColor="#0E8577"
                    />
                    <KpiTile
                        label="Success rate"
                        value={`${kpis.success_rate}%`}
                        sub={`${kpis.failed.toLocaleString()} failed in period`}
                        spark={daily.map(d => {
                            const t = d.succeeded + d.failed;
                            return t === 0 ? 100 : (d.succeeded / t) * 100;
                        })}
                        sparkColor="#1F9D6B"
                        valueClass="text-mapper-teal-dark"
                    />
                    <KpiTile
                        label="Still in flight"
                        value={kpis.in_progress.toLocaleString()}
                        sub="Queued, processing, or awaiting confirm"
                        spark={daily.map(d => d.in_progress)}
                        sparkColor="#3B5BA9"
                        valueClass="text-mapper-blue"
                    />
                    <KpiTile
                        label="Avg. sync time"
                        value={avgLabel}
                        sub={
                            peak
                                ? `Peak day ${peak.label} · ${(
                                    peak.succeeded + peak.failed + peak.ignored + peak.in_progress
                                ).toLocaleString()} records`
                                : 'No completed timing in range'
                        }
                        spark={daily.map(d => d.succeeded)}
                        sparkColor="#6A62B5"
                    />
                </div>

                <div className={`grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-[14px] mb-[14px] ${loading ? 'opacity-60' : ''}`}>
                    <Panel
                        title="Daily volume"
                        subtitle="Click a day to see the records for that day"
                        action={
                            <div className="flex items-center gap-[3px] p-[3px] bg-[#EEF1F5] rounded-[10px]">
                                {([
                                    ['entity', 'By entity'],
                                    ['direction', 'By direction'],
                                    ['outcome', 'By outcome'],
                                ] as const).map(([val, label]) => (
                                    <button key={val} type="button" onClick={() => setBarMode(val)} className={segClass(barMode === val)}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        }
                    >
                        {!hasVolume ? (
                            <EmptyChart loading={loading} />
                        ) : (
                            <GroupedBarChart
                                daily={daily}
                                mode={barMode}
                                height={240}
                                onDayClick={day => openDetail('day', day.date)}
                            />
                        )}
                    </Panel>

                    <Panel title="Outcome mix" subtitle="Click a slice to see the records">
                        {outcomes.length === 0 ? (
                            <EmptyChart loading={loading} />
                        ) : (
                            <DonutChart
                                slices={outcomes}
                                centerLabel="outcomes"
                                onSliceClick={slice => openDetail('outcome', slice.key)}
                            />
                        )}
                    </Panel>
                </div>

                {detailReq && (
                    <div ref={detailPanelRef} className="mb-[14px]">
                        <ChartDetailPanel
                            detail={detail}
                            loading={detailLoading}
                            error={detailError}
                            onClose={() => {
                                setDetailReq(null);
                                setDetail(null);
                                setDetailError(null);
                            }}
                            onGo={goToMapping}
                            onRetry={() => setDetailReq(req => (req ? { ...req } : null))}
                        />
                    </div>
                )}

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-[14px] mb-[14px] ${loading ? 'opacity-60' : ''}`}>
                    <Panel title="Direction split" subtitle="Click a slice to see the records">
                        {directions.length === 0 ? (
                            <EmptyChart loading={loading} />
                        ) : (
                            <>
                                <DonutChart
                                    slices={directions}
                                    size={160}
                                    thickness={24}
                                    centerLabel="records"
                                    onSliceClick={slice => openDetail('direction', slice.key)}
                                />
                                <div className="mt-5 pt-4 border-t border-mapper-border">
                                    <StackedShareBar slices={directions} />
                                </div>
                            </>
                        )}
                    </Panel>

                    <Panel title="Entity mix" subtitle="Click a slice to see the records">
                        {entities.length === 0 ? (
                            <EmptyChart loading={loading} />
                        ) : (
                            <>
                                <DonutChart
                                    slices={entities}
                                    size={160}
                                    thickness={24}
                                    centerLabel="entities"
                                    onSliceClick={slice => openDetail('entity', slice.key)}
                                />
                                <div className="mt-5 pt-4 border-t border-mapper-border">
                                    <StackedShareBar slices={entities} />
                                </div>
                            </>
                        )}
                    </Panel>
                </div>

                <div className="bg-mapper-white border border-mapper-border rounded-[14px] px-[22px] py-[18px]">
                    <div className="text-[12px] font-bold tracking-[0.5px] text-mapper-muted-4 mb-3">AT A GLANCE</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Insight
                            title="Healthiest slice"
                            body={
                                !hasVolume
                                    ? 'No sync activity in this range for the selected filters.'
                                    : kpis.success_rate >= 90
                                        ? `Success sits at ${kpis.success_rate}% for this filter set — well above the soft target of 90%.`
                                        : `Success is ${kpis.success_rate}%. Tighten mappings or clear blocking errors to lift the rate.`
                            }
                        />
                        <Insight
                            title="Heaviest direction"
                            body={
                                directions.length
                                    ? (() => {
                                        const sorted = [...directions].sort((a, b) => b.value - a.value);
                                        const top = sorted[0];
                                        const total = directions.reduce((s, x) => s + x.value, 0);
                                        const pct = total ? Math.round((top.value / total) * 100) : 0;
                                        return `${top.label} carries ${pct}% of volume (${top.value.toLocaleString()} records).`;
                                    })()
                                    : 'No direction data in range.'
                            }
                        />
                        <Insight
                            title="Who this is for"
                            body="Practice-scoped users only see this location’s aggregates. Sync Tracking is the per-record ops console."
                        />
                    </div>
                </div>
            </div>

            <AnalyticsHelpDialog
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                practiceName={currentLocation?.name}
                locationId={locationId || undefined}
            />
        </div>
    );
};

function KpiTile({
    label,
    value,
    sub,
    spark,
    sparkColor,
    valueClass = 'text-mapper-text',
}: {
    label: string;
    value: string;
    sub: string;
    spark: number[];
    sparkColor: string;
    valueClass?: string;
}) {
    return (
        <div className="bg-mapper-white border border-mapper-border rounded-[14px] px-[18px] py-4">
            <div className="flex items-start justify-between gap-3 mb-3">
                <span className="text-[13px] font-semibold text-mapper-body-text">{label}</span>
                <Sparkline values={spark} color={sparkColor} />
            </div>
            <div className={`font-mono text-[28px] font-bold tracking-[-1px] leading-none ${valueClass}`}>{value}</div>
            <div className="text-[11.5px] text-mapper-muted-3 mt-2 leading-snug">{sub}</div>
        </div>
    );
}

function Panel({
    title,
    subtitle,
    children,
    action,
}: {
    title: string;
    subtitle: string;
    children: React.ReactNode;
    action?: React.ReactNode;
}) {
    return (
        <div className="bg-mapper-white border border-mapper-border rounded-[14px] px-[20px] py-[18px]">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <h2 className="text-[15px] font-bold tracking-[-0.2px] text-mapper-text mb-0.5">{title}</h2>
                    <p className="text-[12.5px] text-mapper-muted-2">{subtitle}</p>
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}

function Insight({ title, body }: { title: string; body: string }) {
    return (
        <div>
            <div className="text-[13px] font-semibold text-mapper-body-text-3 mb-1">{title}</div>
            <p className="text-[13px] text-mapper-muted leading-relaxed">{body}</p>
        </div>
    );
}

const CloseIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
        <path d="M6 6l12 12M18 6L6 18" />
    </svg>
);

const ArrowIcon = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

function ChartDetailPanel({
    detail,
    loading,
    error,
    onClose,
    onGo,
    onRetry,
}: {
    detail: SyncAnalyticsDetailResponse | null;
    loading: boolean;
    error: string | null;
    onClose: () => void;
    onGo: (route: string) => void;
    onRetry: () => void;
}) {
    const shownNote =
        detail && detail.total > detail.shown
            ? `Showing ${detail.shown.toLocaleString()} of ${detail.total.toLocaleString()} records`
            : detail
                ? `${detail.total.toLocaleString()} record${detail.total === 1 ? '' : 's'}`
                : '';

    return (
        <div className="border-[1.5px] border-mapper-teal-border bg-mapper-teal-bg-2 rounded-[14px] overflow-hidden">
            <div className="flex items-start justify-between gap-4 px-[18px] pt-[14px] pb-[12px]">
                <div className="min-w-0">
                    <div className="text-[14.5px] font-extrabold tracking-[-0.2px] leading-tight">
                        {loading && !detail ? 'Loading records…' : (detail?.title ?? 'Selection')}
                    </div>
                    <div className="text-[12px] text-mapper-muted-2 mt-0.5">
                        {[detail?.subtitle, shownNote].filter(Boolean).join(' · ')
                            || 'Records for this chart selection'}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    title="Dismiss"
                    className="w-[28px] h-[28px] flex items-center justify-center rounded-[8px] text-mapper-muted-3 hover:bg-mapper-teal-bg-3 hover:text-mapper-body-text shrink-0"
                >
                    {CloseIcon}
                </button>
            </div>

            <div className="bg-mapper-white border-t border-mapper-teal-border px-[18px] py-4">
                {loading && !detail && <DetailSkeleton />}

                {!loading && error && (
                    <div className="text-[13px] text-mapper-red">
                        {error}{' '}
                        <button type="button" onClick={onRetry} className="underline font-semibold text-mapper-teal-hover">
                            Try again
                        </button>
                    </div>
                )}

                {detail && (
                    <div className={loading ? 'opacity-60' : ''}>
                        {detail.total === 0 ? (
                            <p className="text-[13.5px] text-mapper-muted leading-relaxed">
                                No records match this selection for the current filters.
                            </p>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {detail.mapping_actions.length > 0 && (
                                    <div className="flex flex-col gap-2">
                                        <div className="text-[11px] font-bold tracking-[0.4px] text-mapper-muted-3">
                                            FIX MAPPINGS
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {detail.mapping_actions.map(a => (
                                                <button
                                                    key={a.key}
                                                    type="button"
                                                    title={a.brief || a.label}
                                                    onClick={() => onGo(a.mapping_route)}
                                                    className="flex items-center gap-[7px] px-[11px] py-[7px] rounded-[9px] border border-mapper-teal-border bg-mapper-teal-bg text-mapper-teal-hover text-[12.5px] font-semibold hover:bg-mapper-teal-bg-3"
                                                >
                                                    <span>{a.mapping_cta}</span>
                                                    <span className="font-mono text-[11.5px] opacity-80">
                                                        {a.count}
                                                    </span>
                                                    {ArrowIcon}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <div className="text-[11px] font-bold tracking-[0.4px] text-mapper-muted-3 mb-2">
                                        RECORDS
                                    </div>
                                    <div className="border border-mapper-border rounded-[11px] overflow-hidden">
                                        <div className="divide-y divide-mapper-border-4 max-h-[360px] overflow-y-auto">
                                            {detail.items.map(item => {
                                                const sv = statusView(item.status);
                                                const dv = dirView(item.direction);
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className="flex items-start gap-3 px-[14px] py-[11px]"
                                                    >
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                                <span className="text-[13.5px] font-bold text-mapper-text truncate">
                                                                    {item.label}
                                                                </span>
                                                                <span className={`px-[7px] py-[2px] rounded-[6px] text-[10.5px] font-bold ${sv.pill}`}>
                                                                    {item.status_label || sv.label}
                                                                </span>
                                                                <span className="text-[11.5px] text-mapper-muted-3 font-medium">
                                                                    {dv.label}
                                                                </span>
                                                            </div>
                                                            {item.failure_label ? (
                                                                <div className="text-[12.5px] text-mapper-body-text-3">
                                                                    {item.failure_label}
                                                                </div>
                                                            ) : null}
                                                            {item.error_message ? (
                                                                <div className="text-[12px] text-mapper-muted mt-0.5 leading-snug line-clamp-2">
                                                                    {item.error_message}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                        {item.mapping_route && item.mapping_cta && (
                                                            <button
                                                                type="button"
                                                                onClick={() => onGo(item.mapping_route!)}
                                                                className="shrink-0 mt-0.5 flex items-center gap-[5px] px-[10px] py-[6px] rounded-[8px] border border-mapper-teal-border bg-mapper-teal-bg text-mapper-teal-hover text-[11.5px] font-semibold hover:bg-mapper-teal-bg-3"
                                                            >
                                                                <span className="hidden sm:inline">{item.mapping_cta}</span>
                                                                <span className="sm:hidden">Fix</span>
                                                                {ArrowIcon}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {detail.total > detail.shown && (
                                        <p className="text-[12px] text-mapper-muted-3 mt-2">
                                            Showing the newest {detail.shown.toLocaleString()} of{' '}
                                            {detail.total.toLocaleString()} in this selection.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

const DetailSkeleton: React.FC = () => (
    <div className="animate-pulse flex flex-col gap-2">
        {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-[44px] rounded-[8px] bg-mapper-border-4" />
        ))}
    </div>
);

function EmptyChart({ loading }: { loading?: boolean }) {
    return (
        <div className="h-[180px] flex items-center justify-center text-[13px] text-mapper-muted-3 border border-dashed border-mapper-border rounded-[12px]">
            {loading ? 'Loading…' : 'No data for this filter combination'}
        </div>
    );
}
